// Folder-upload helpers: turn a dropped or picked directory into a flat list of
// { file, segments } where `segments` is the folder path the file sits in,
// relative to what the user selected.
//
// Two input paths, deliberately ranked:
//   1. <input webkitdirectory> — the PRIMARY path. Support is consistent across
//      browsers and it hands us webkitRelativePath directly.
//   2. Drag-and-drop of a directory via DataTransferItem.webkitGetAsEntry() —
//      an ENHANCEMENT. Behaviour varies between browsers, so callers must be
//      able to fall back to the picker when it is unavailable.

// Files an OS scatters through a directory tree that nobody wants archived.
const JUNK_NAMES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini', '.localized']);

export function isJunkFile(name) {
  const n = String(name || '').toLowerCase();
  // AppleDouble resource forks are '._' + the real filename.
  return JUNK_NAMES.has(n) || n.startsWith('._');
}

// Yield to the browser so a large tree doesn't lock the UI mid-walk.
function breathe() {
  return new Promise(resolve => setTimeout(resolve, 0));
}

const WALK_BATCH = 200;   // files processed between yields

// ─── Path 1: <input webkitdirectory> ──────────────────────────────────────────

// webkitRelativePath looks like 'June Shoot/Cam A/clip.mp4' — the first segment
// is the directory the user picked, which we keep so the tree is recreated
// under the destination rather than having its top level flattened away.
export async function entriesFromPicker(fileList, onProgress) {
  const out = [];
  const files = Array.from(fileList || []);
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const rel  = file.webkitRelativePath || file.name;
    const parts = rel.split('/').filter(Boolean);
    const segments = parts.slice(0, -1);
    out.push({ file, segments, name: file.name });
    if (i % WALK_BATCH === WALK_BATCH - 1) {
      onProgress?.(out.length);
      await breathe();
    }
  }
  onProgress?.(out.length);
  return out;
}

// ─── Path 2: drag-and-drop directory traversal ────────────────────────────────

export function supportsDirectoryDrop(dataTransfer) {
  const items = dataTransfer?.items;
  if (!items || !items.length) return false;
  const getAsEntry = items[0].webkitGetAsEntry || items[0].getAsEntry;
  return typeof getAsEntry === 'function';
}

// Does the drop actually contain a directory? A plain multi-file drop should go
// down the ordinary file path, not the folder path.
export function dropContainsDirectory(dataTransfer) {
  if (!supportsDirectoryDrop(dataTransfer)) return false;
  for (const item of dataTransfer.items) {
    const entry = (item.webkitGetAsEntry || item.getAsEntry).call(item);
    if (entry?.isDirectory) return true;
  }
  return false;
}

function readAllDirectoryEntries(reader) {
  // readEntries() returns at most ~100 entries per call and must be called
  // repeatedly until it yields an empty array. Missing this silently truncates
  // large folders — a classic and very quiet bug.
  return new Promise((resolve, reject) => {
    const all = [];
    const readBatch = () => {
      reader.readEntries(batch => {
        if (!batch.length) return resolve(all);
        all.push(...batch);
        readBatch();
      }, reject);
    };
    readBatch();
  });
}

function fileFromEntry(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

export async function entriesFromDataTransfer(dataTransfer, onProgress) {
  if (!supportsDirectoryDrop(dataTransfer)) return null;

  // Snapshot the entries synchronously — the DataTransfer is neutered as soon
  // as we await.
  const roots = [];
  for (const item of dataTransfer.items) {
    const entry = (item.webkitGetAsEntry || item.getAsEntry).call(item);
    if (entry) roots.push(entry);
  }
  if (!roots.length) return null;

  const out = [];
  // Iterative queue rather than recursion: a deep tree would otherwise risk the
  // call stack, and this makes batching straightforward.
  const queue = roots.map(entry => ({ entry, segments: [] }));

  while (queue.length) {
    const { entry, segments } = queue.shift();
    if (entry.isFile) {
      try {
        const file = await fileFromEntry(entry);
        out.push({ file, segments, name: entry.name });
      } catch {
        // A file we can't read (permissions, moved mid-walk) is skipped rather
        // than failing the whole drop.
      }
      if (out.length % WALK_BATCH === 0) { onProgress?.(out.length); await breathe(); }
    } else if (entry.isDirectory) {
      const children = await readAllDirectoryEntries(entry.createReader());
      const childSegments = [...segments, entry.name];
      for (const child of children) queue.push({ entry: child, segments: childSegments });
      await breathe();
    }
  }

  onProgress?.(out.length);
  return out;
}

// ─── Pre-flight summary ───────────────────────────────────────────────────────

// Splits the walked entries into what will be uploaded and what was skipped,
// and derives the distinct folder-path set to hand to /folders/resolve.
export function buildPreflight(entries) {
  const kept = [];
  let skippedJunk = 0;

  for (const e of entries) {
    if (isJunkFile(e.name)) { skippedJunk++; continue; }
    kept.push(e);
  }

  // Distinct folder paths, including intermediate levels — resolve needs the
  // leaf paths and derives prefixes itself, but sending only leaves means an
  // empty intermediate directory is never created. Sending every prefix keeps
  // the structure faithful.
  const pathKeys = new Map();
  for (const e of kept) {
    for (let i = 1; i <= e.segments.length; i++) {
      const prefix = e.segments.slice(0, i);
      pathKeys.set(prefix.join('/'), prefix);
    }
  }
  const folderPaths = [...pathKeys.values()];

  const totalBytes = kept.reduce((sum, e) => sum + (e.file?.size || 0), 0);
  const maxDepth = kept.reduce((m, e) => Math.max(m, e.segments.length), 0);

  return {
    entries: kept,
    folderPaths,
    fileCount: kept.length,
    folderCount: folderPaths.length,
    totalBytes,
    maxDepth,
    skippedJunk,
  };
}

export function formatBytes(b) {
  const n = Number(b);
  if (!n) return '0 B';
  if (n < 1024)      return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n < 1024 ** 4) return `${(n / 1024 ** 3).toFixed(2)} GB`;
  return `${(n / 1024 ** 4).toFixed(2)} TB`;
}
